import {Button} from '@chakra-ui/react';
import {Input, InputProps} from '@chakra-ui/input';
import {Box, Flex, SimpleGrid, Text} from '@chakra-ui/layout';
import {useEffect, useState} from 'react';
import {QuatitySelectorProps} from '../../hooks/quantity-selector';
import {getPlural} from '../../utils/utils';
import {BenefitCard} from './benefit-card';
import {useAppToast} from '../../hooks/app-toast';
import {AmountInput} from './amount-input';
import {BenefitTierRecord} from '../../graphql/datocms-generated';

interface QuantityButtonProps {
  onClick: () => void;
  children: React.ReactNode;
  borderColor: string;
  size?: 'md' | 'lg';
}

export const QuantityButton: React.FC<QuantityButtonProps> = ({
  onClick,
  children,
  borderColor,
  size = 'lg',
}) => {
  return (
    <Button
      size={size}
      px={size === 'md' ? 4 : 6}
      height="100%"
      border="1px solid"
      bgColor="calcBtnGrey"
      borderColor={borderColor}
      color="tone3"
      fontSize={size === 'md' ? '14px' : '21px'}
      zIndex="docked"
      textStyle="h6"
      onClick={onClick}
    >
      {children}
    </Button>
  );
};

interface QuantitySelectorProps {
  quantityInput: {
    value: number | '';
  };
  size?: 'md' | 'lg';
  showMaxPiecesError: boolean;
  onClickDecrement: () => void;
  onClickIncrement: () => void;
  additionalInputStyles?: InputProps;
  maxPieces: number;
}

export const QuantitySelector: React.FC<QuantitySelectorProps> = ({
  quantityInput,
  size = 'lg',
  showMaxPiecesError,
  onClickDecrement,
  onClickIncrement,
  additionalInputStyles,
  maxPieces,
}: QuantitySelectorProps) => {
  const toast = useAppToast();

  useEffect(() => {
    if (showMaxPiecesError)
      toast.showInfoToast(
        `Each user can only have up to ${maxPieces} pieces (10%).`,
        'Maximum number of pieces reached',
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMaxPiecesError]);

  return (
    <Flex flexDir="row" height={size === 'lg' ? '65px' : '45px'}>
      <Flex color="tone3">
        <QuantityButton
          onClick={onClickDecrement}
          borderColor="tone2"
          size={size}
        >
          -
        </QuantityButton>
        <Box
          width={size === 'md' ? '65px' : '90px'}
          borderTop="1px solid"
          borderBottom="1px solid"
          borderColor="tone2"
          mx="-12px"
        >
          <Input
            height="100%"
            width="100%"
            variant="copper"
            textStyle={size === 'md' ? 'body3' : 'h6'}
            fontSize={size === 'md' ? '14px' : '21px'}
            color="tone3"
            px={2}
            {...quantityInput}
            textAlign="center"
            type="number"
            bgColor="white"
            {...additionalInputStyles}
            // converts the leading 0 numbers into human-readable ones
            value={Number(quantityInput.value)}
          />
        </Box>
        <QuantityButton
          onClick={onClickIncrement}
          borderColor="tone2"
          size={size}
        >
          +
        </QuantityButton>
      </Flex>
    </Flex>
  );
};

export interface PricingProps {
  benefits?: BenefitTierRecord[];
  quatitySelectorProps: QuatitySelectorProps;
  goToCheckout: () => void;
  addPieces?: (piecesNumber: number) => void;
  maxPieces: number;
  isDisabled?: boolean;
  currencySymbol: string;
  currencyPostFix: string;
  hasPartners: boolean;
}

const BuyGiftInfoBox = () => {
  return (
    <Flex
      flex={0.7}
      border="2px solid #1ABAB0"
      borderRadius={8}
      backgroundColor="white"
      padding="8px 16px"
      mt={{base: 2, md: 0}}
    >
      <Text color="#1ABAB0" textStyle="body3">
        You can buy pieces as a gift at checkout for anyone over the age of 18!
        You tell us who it’s for, and we’ll email you an invite code and
        invitation card you can print off and give as a gift.
      </Text>
    </Flex>
  );
};

export const PiecesCalculator: React.FC<PricingProps> = ({
  benefits,
  quatitySelectorProps,
  goToCheckout,
  addPieces,
  maxPieces,
  isDisabled,
  currencySymbol,
  currencyPostFix,
  hasPartners,
}: PricingProps) => {
  const [reveal, setReveal] = useState<boolean>(false);
  const {
    quantity,
    loading,
    amountInput,
    quantityInput,
    incrementQuantity,
    showRoundingMsg,
    showMaxPiecesError,
    decrementQuantity,
  } = quatitySelectorProps;
  return (
    <>
      <Flex
        pt={{base: '40px', md: '48px'}}
        pb={hasPartners ? {base: '40px'} : {base: '50px'}}
        px={{base: 6, md: '64px'}}
        mt={{base: 0, md: '32px', lg: '-24px'}}
        flexDir="column"
        flex={1}
        layerStyle="card"
        borderBottomRadius={hasPartners ? '0' : {base: '12px', md: '24px'}}
        borderTopRadius={{base: '12px', md: '24px'}}
        position="relative"
      >
        <Flex
          textAlign={{base: 'center', md: 'left'}}
          flex={1}
          justifyContent="space-between"
          alignItems="center"
          flexDir={{base: 'column', md: 'row'}}
        >
          <Text
            textStyle={{base: 'h6'}}
            color="darkGrey"
            alignSelf="flex-start"
          >
            How many pieces would you like to own?
          </Text>
          {reveal ? (
            <BuyGiftInfoBox />
          ) : (
            <Text
              textStyle={{base: 'body3', md: 'body2'}}
              textDecor="underline"
              as="button"
              mt={{base: 4, md: 0}}
              onClick={() => setReveal(true)}
            >
              Buying as a gift?
            </Text>
          )}
        </Flex>
        <Flex flex={1} mt={6} justifyContent="space-between">
          <Flex
            flex={1}
            justifyContent={{base: 'inherit', md: 'space-between'}}
            flexDir={{base: 'column', md: 'row'}}
          >
            <Flex flexDir={{base: 'column', md: 'row'}}>
              <Box pos="relative">
                <Text textStyle="body3" color="tone3" mb={1}>
                  Enter your amount
                </Text>
                <AmountInput
                  loading={loading}
                  amountInput={amountInput}
                  currency={currencySymbol}
                  currencyPostfix={currencyPostFix}
                />
                {showRoundingMsg ? (
                  <Text
                    color="stampRed"
                    textStyle="body1"
                    pos="absolute"
                    mt={2}
                  >
                    This rounds up to {quantity} piece{getPlural(quantity)}
                  </Text>
                ) : null}
              </Box>
              <Flex
                flexDir="column"
                my={{base: '32px', md: 0}}
                alignItems={{base: 'center', md: 'inherit'}}
                mx={4}
              >
                <Text textStyle="body3" color="tone3" mb={1}>
                  Number of pieces
                </Text>
                <QuantitySelector
                  quantityInput={quantityInput}
                  showMaxPiecesError={showMaxPiecesError}
                  onClickDecrement={decrementQuantity}
                  onClickIncrement={incrementQuantity}
                  maxPieces={maxPieces}
                />
              </Flex>
            </Flex>
            <Flex alignSelf={{base: 'center', md: 'flex-end'}}>
              <Button
                colorScheme="aqua"
                type="submit"
                size="lg"
                isDisabled={!quantity || isDisabled}
                maxW={{base: 'none', md: '290px'}}
                onClick={goToCheckout}
                py="18px"
                px="72px"
              >
                Go to checkout
              </Button>
            </Flex>
          </Flex>
        </Flex>
        {benefits && benefits.length > 0 && (
          <>
            <Text textStyle="button" mt="57px" textAlign="center">
              More Pieces means more benefits:
            </Text>
            <Flex flex={1} mt={4} justifyContent="center">
              <SimpleGrid spacing={4} columns={{base: 1, lg: 3}}>
                {benefits &&
                  benefits.map((benefit: BenefitTierRecord) => (
                    <BenefitCard
                      key={benefit.quantity}
                      quantity={benefit.quantity}
                      hoverAction={
                        addPieces
                          ? () => addPieces(benefit.quantity)
                          : undefined
                      }
                      textColor={benefit.textColor?.hex || ''}
                      coinColor={benefit.coinColor || ''}
                      iconSrc={benefit?.tierIcon?.url || ''}
                      benefits={benefit.benefitList}
                      imageSrc={
                        benefit?.benefitImage?.responsiveImage?.src || ''
                      }
                    />
                  ))}
              </SimpleGrid>
            </Flex>
          </>
        )}
      </Flex>
    </>
  );
};
